// db.js
import { INDEXED_DB_VERSION } from "@configs/runtimeConfig";
import { openDB } from "idb";
import { convertLocalDateStringToUTC } from "./common";

import { LOCAL_STORAGE, LOGIN_CONTEXT } from "@constants/index";
import { compareSbomListOrder } from "@constants/sbom";
import { setClientConfigValue } from "@contexts/ClientConfigContext";
import { getLocalStorage } from "@utils/local-storage";

const DB_NAME = "cscDB";
const DB_VERSION = Number(INDEXED_DB_VERSION) || 1;

export const STORES = {
  PROJECTS: "projects",
  VERIFICATION_REPORTS: "verification_reports",
  NOTICE_INFOS: "notice_infos",
  MERGED_SBOMS: "merged_sboms",
  PREVIEWS: "project_previews",
  REQUESTS: "actions",
  IMPORT_COMPONENTS: "import_components",

  VULNERABILITY_STATUSES: "vulnerability_statuses",
  BULLETINS: "bulletins",
  QNAS: "qnas",
  FAQS: "faqs",
  ARCHIVES: "archives",

  USERS: "users",
  SBOMS: "sboms",
  SYNC_META: "sync_meta",
};

/** Timestamp field used for sync metadata maxUpdatedAt per store. */
export const STORE_SYNC_METADATA_TIME_FIELD = {
  [STORES.PROJECTS]: "updatedAt",
  [STORES.VERIFICATION_REPORTS]: "updatedAt",
  [STORES.NOTICE_INFOS]: "updatedAt",
  [STORES.MERGED_SBOMS]: "updatedAt",
  [STORES.PREVIEWS]: "updatedAt",
  [STORES.REQUESTS]: "updatedAt",
  [STORES.IMPORT_COMPONENTS]: "updatedAt",
  // vulnerability_statuses list payload only exposes createdAt
  [STORES.VULNERABILITY_STATUSES]: "createdAt",
  [STORES.BULLETINS]: "updatedAt",
  [STORES.QNAS]: "updatedAt",
  [STORES.FAQS]: "updatedAt",
  [STORES.ARCHIVES]: "updatedAt",
  [STORES.USERS]: "updatedAt",
  [STORES.SBOMS]: "updatedAt",
};

export function toSyncMetadataEpochMs(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

const STORE_SEARCH_FIELDS = {
  [STORES.PROJECTS]: [
    "projectId",
    "projectName",
    "projectManagerName",
    "productName",
    "assetClassificationName",
    "verificationStatus",
    "createrDeptCdName",
    "createrName",
    "verificationStatusCdName",
  ],
  [STORES.VERIFICATION_REPORTS]: [
    "verificationReportId",
    "verificationReportName",
    "verificationReportStatusCdName",
    "verificationVersion",
    "createrDeptCdName",
    "createrName",
  ],
  [STORES.NOTICE_INFOS]: [
    "noticeInfoId",
    "noticeInfoName",
    "noticeInfoStatusCdName",
    "createrDeptCdName",
    "createrName",
  ],
  [STORES.MERGED_SBOMS]: ["mergedName", "createrName"],
  [STORES.PREVIEWS]: ["projectPreviewName", "licensePolicyName", "createrName"],
  [STORES.REQUESTS]: [
    "actionSeq",
    "actionId",
    "actionStatus",
    "realFileName",
    "projectName",
    "createrName",
    "assigneeName",
    "scanTypeCdName",
    "verificationTypeCd",
  ],
  [STORES.IMPORT_COMPONENTS]: [
    "licensePolicyConflictFlag",
    // "complexComponent",
    "component",
    "componentVersion",
    "componentGroup",
    "realFileName",
    "importComponentStatusCdName",
    "approverName",
  ],
  [STORES.VULNERABILITY_STATUSES]: [
    "vulnerabilityLevel",
    "doneObligationType",
    "conflictStatus",
    "projectName",
    "verificationFileName",
    "verificationTypeCd",
    "verificationVersion",
    // "componentInformation",
    "component",
    "componentVersion",
    "componentLicense",
    "cvssScore",
    "security",
    "vulnerabilityLevelMark",
    "projectCreatedAt",
  ],
  [STORES.BULLETINS]: ["bulletinSeq", "title", "viewCnt", "createrDeptCdName", "createrName"],
  [STORES.QNAS]: ["qnaSeq", "title", "qnaStatusCdName", "viewCnt", "createrName"],
  [STORES.FAQS]: ["faqSeq", "question", "viewCnt", "createrName"],
  [STORES.ARCHIVES]: ["archiveSeq", "title", "viewCnt", "createrName"],

  [STORES.USERS]: ["userId", "userName", "deptCdName", "email", "roleCdName", "enableFlag", "createrName"],
  [STORES.SBOMS]: ["sbomName", "remark", "sbomTypeCdName", "createrName"],
};

const STORE_FILTER_FIELDS = {
  [STORES.PROJECTS]: {
    // Date range filters
    productName: { type: "select", key: "productName" },
    assetClassificationName: { type: "select", key: "assetClassificationName" },
    projectManagerId: { type: "select", key: "projectManagerId" },
    createrDeptCd: { type: "select", key: "createrDeptCd" },
    createrId: { type: "select", key: "createrId" },
    binaryVerificationStatusCd: { type: "select", key: "binaryVerificationStatusCd" },
    sourceCodeVerificationStatusCd: { type: "select", key: "sourceCodeVerificationStatusCd" },
    binaryVerificationStatusCdName: { type: "select", key: "binaryVerificationStatusCdName" },
    sourceCodeVerificationStatusCdName: { type: "select", key: "sourceCodeVerificationStatusCdName" },
    sbomVerificationStatusCd: { type: "select", key: "sbomVerificationStatusCd" },
    clarityAirStatusCd: { type: "select", key: "clarityAirStatusCd" },
    createdAtFrom: { type: "date", key: "createdAt", operator: ">=" },
    createdAtTo: { type: "date", key: "createdAt", operator: "<=" },
    completionRequestDt: { type: "date", key: "completionRequestDt", operator: "=" },
  },
  [STORES.VERIFICATION_REPORTS]: {
    verificationReportStatusCd: { type: "select", key: "verificationReportStatusCd" },
    verificationToolCd: { type: "select", key: "verificationToolCd" },
    verificationReportResultCd: { type: "select", key: "verificationReportResultCd" },
    projectManagerId: { type: "select", key: "projectManagerId" },
    createrId: { type: "select", key: "createrId" },
    createrDeptCd: { type: "select", key: "createrDeptCd" },
    createdAtFrom: { type: "date", key: "createdAt", operator: ">=" },
    createdAtTo: { type: "date", key: "createdAt", operator: "<=" },
  },
  [STORES.NOTICE_INFOS]: {
    noticeInfoStatusCd: { type: "select", key: "noticeInfoStatusCd" },
    createrId: { type: "select", key: "createrId" },
    createrDeptCd: { type: "select", key: "createrDeptCd" },
    createdAtFrom: { type: "date", key: "createdAt", operator: ">=" },
    createdAtTo: { type: "date", key: "createdAt", operator: "<=" },
  },
  [STORES.MERGED_SBOMS]: {
    createrId: { type: "select", key: "createrId" },
    createdAtFrom: { type: "date", key: "createdAt", operator: ">=" },
    createdAtTo: { type: "date", key: "createdAt", operator: "<=" },
  },
  [STORES.PREVIEWS]: {
    createrId: { type: "select", key: "createrId" },
    createdAtFrom: { type: "date", key: "createdAt", operator: ">=" },
    createdAtTo: { type: "date", key: "createdAt", operator: "<=" },
  },
  [STORES.REQUESTS]: {
    actionStatus: { type: "select", key: "actionStatus" },
    assigneeId: { type: "select", key: "assigneeId" },
    createrId: { type: "select", key: "createrId" },
    scanTypeCdName: { type: "select", key: "scanTypeCdName" },
    verificationTypeCd: { type: "select", key: "verificationTypeCd" },
    completionRequestDt: { type: "date", key: "completionRequestDt", operator: "=" },
  },
  [STORES.IMPORT_COMPONENTS]: {
    importComponentStatusCd: { type: "select", key: "importComponentStatusCd" },
    approverId: { type: "select", key: "approverId" },
    updatedAtFrom: { type: "date", key: "updatedAt", operator: ">=" },
    updatedAtTo: { type: "date", key: "updatedAt", operator: "<=" },
    allowFlag: { type: "select", key: "allowFlag" },
  },

  [STORES.BULLETINS]: {
    postClassCd: { type: "select", key: "postClassCd" },
    postTypeCd: { type: "select", key: "postTypeCd" },
    mainPopupFlag: { type: "select", key: "mainPopupFlag" },
    createrId: { type: "select", key: "createrId" },
    createrDeptCd: { type: "select", key: "createrDeptCd" },
    postStartDtFrom: { type: "date", key: "postStartDt", operator: ">=" },
    postStartDtTo: { type: "date", key: "postStartDt", operator: "<=" },
    createdAtFrom: { type: "date", key: "createdAt", operator: ">=" },
    createdAtTo: { type: "date", key: "createdAt", operator: "<=" },
  },
  [STORES.QNAS]: {
    qaStatus: { type: "select", key: "qaStatus" },
    createrId: { type: "select", key: "createrId" },
    openFlag: { type: "select", key: "openFlag" },
    answeredAtFrom: { type: "date", key: "answeredAt", operator: ">=" },
    answeredAtTo: { type: "date", key: "answeredAt", operator: "<=" },
    createdAtFrom: { type: "date", key: "createdAt", operator: ">=" },
    createdAtTo: { type: "date", key: "createdAt", operator: "<=" },
  },
  [STORES.FAQS]: {
    createrId: { type: "select", key: "createrId" },
    createdAtFrom: { type: "date", key: "createdAt", operator: ">=" },
    createdAtTo: { type: "date", key: "createdAt", operator: "<=" },
  },
  [STORES.ARCHIVES]: {
    createrId: { type: "select", key: "createrId" },
    createdAtFrom: { type: "date", key: "createdAt", operator: ">=" },
    createdAtTo: { type: "date", key: "createdAt", operator: "<=" },
  },

  [STORES.USERS]: {
    deptCd: { type: "select", key: "deptCd" },
    roleCd: { type: "select", key: "roleCd" },
    createrId: { type: "select", key: "createrId" },
    createdAtFrom: { type: "date", key: "createdAt", operator: ">=" },
    createdAtTo: { type: "date", key: "createdAt", operator: "<=" },
  },
  [STORES.SBOMS]: {
    sbomTypeCd: { type: "select", key: "sbomTypeCd" },
    createrId: { type: "select", key: "createrId" },
    createdAtFrom: { type: "date", key: "createdAt", operator: ">=" },
    createdAtTo: { type: "date", key: "createdAt", operator: "<=" },
  },
  [STORES.VULNERABILITY_STATUSES]: {
    cve: { type: "text", key: "securities" },
    createdAtFrom: { type: "date", key: "createdAt", operator: ">=" },
    createdAtTo: { type: "date", key: "createdAt", operator: "<=" },
    cvssScore: { type: "select", key: "cvssScore" },
    doneObligationType: { type: "select", key: "doneObligationType" },
    conflictStatus: { type: "select", key: "conflictStatus" },
    verificationTypeCd: { type: "select", key: "verificationTypeCd" },
    vulnerabilityLevel: { type: "select", key: "vulnerabilityLevel" },
  },
};

const STORE_CUSTOM_SORT = {
  [STORES.SBOMS]: compareSbomListOrder,
};

const STORE_PERMISSIONS = {
  [STORES.PROJECTS]: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER],
  [STORES.VERIFICATION_REPORTS]: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER],
  [STORES.NOTICE_INFOS]: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER],
  [STORES.MERGED_SBOMS]: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER],
  [STORES.PREVIEWS]: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER],

  [STORES.REQUESTS]: [LOGIN_CONTEXT.SUPERUSER],
};

export async function getDB() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      Object.values(STORES).forEach((store) => {
        if (!db.objectStoreNames.contains(store)) {
          db.createObjectStore(store, { keyPath: "id" });
        }
      });
    },
  });
}

export async function saveIndexDBData(storeName, data, idKey = "id") {
  const db = await getDB();
  const tx = db.transaction(storeName, "readwrite");
  const store = tx.objectStore(storeName);

  // Add unique id if it doesn't exist (for projects, use projectSeq as id)
  const dataWithIds = data.map((item, index) => ({
    ...item,
    id: item[idKey],
  }));

  await Promise.all(dataWithIds.map((item) => store.put(item)));
  await tx.done;
}

export async function getIndexDBData(storeName, options = {}) {
  const db = await getDB();
  const records = await db.getAll(storeName);

  const { pageSize, startRow = 0, filters = {}, sortKey = "id", sortOrder = "desc" } = options;

  let result = Array.isArray(records) ? [...records] : [];
  const { search, ...restFilters } = filters || {};

  if (restFilters && Object.keys(restFilters).length > 0) {
    const filterFields = STORE_FILTER_FIELDS[storeName] || {};

    // Pre-convert date filters to avoid repeated conversions in the filter loop
    const convertedDateFilters = {};
    Object.entries(restFilters).forEach(([key, expected]) => {
      if (expected === undefined || expected === null || expected === "") {
        return;
      }

      const filterConfig = filterFields[key];
      if (filterConfig?.type === "date") {
        let timeString;
        switch (filterConfig.operator) {
          case ">=":
            timeString = "T00:00:00";
            break;
          case "<=":
            timeString = "T23:59:59";
            break;
          case "=":
            // For equality, we need both start and end dates
            convertedDateFilters[key] = {
              ...filterConfig,
              filterDateStart: convertLocalDateStringToUTC(expected + "T00:00:00"),
              filterDateEnd: convertLocalDateStringToUTC(expected + "T23:59:59"),
            };
            return;
          default:
            timeString = "T00:00:00";
        }

        convertedDateFilters[key] = {
          ...filterConfig,
          filterDate: convertLocalDateStringToUTC(expected + timeString),
        };
      }
    });

    result = result.filter((item) =>
      Object.entries(restFilters).every(([key, expected]) => {
        if (expected === undefined || expected === null || expected === "") {
          return true;
        }
        const filterConfig = filterFields[key];
        const filterKey = filterConfig?.key || key;

        const value = item?.[filterKey];
        if (!filterConfig) {
          // Default to contains search if no specific filter config
          return String(value ?? "")
            .toLowerCase()
            .includes(String(expected).toLowerCase());
        }

        // Apply specific filter logic based on type and operator
        if (filterConfig.type === "date") {
          const itemDate = item[filterConfig.key];
          const itemDateTime = new Date(itemDate);

          const convertedFilter = convertedDateFilters[key];
          if (convertedFilter) {
            if (convertedFilter.operator === "=") {
              return itemDateTime >= convertedFilter.filterDateStart && itemDateTime <= convertedFilter.filterDateEnd;
            } else {
              switch (convertedFilter.operator) {
                case ">=":
                  return itemDateTime >= convertedFilter.filterDate;
                case "<=":
                  return itemDateTime <= convertedFilter.filterDate;
                default:
                  return itemDateTime >= convertedFilter.filterDate;
              }
            }
          }
        } else if (filterConfig.type === "select") {
          return String(value ?? "").toLowerCase() === String(expected).toLowerCase();
        } else {
          // Default to contains search
          return String(value ?? "")
            .toLowerCase()
            .includes(String(expected).toLowerCase());
        }
      }),
    );
  }

  const searchFields = STORE_SEARCH_FIELDS[storeName];

  if (searchFields && typeof search === "string" && search.trim() !== "") {
    const searchLower = search.trim().toLowerCase();
    result = result.filter((item) =>
      searchFields.some((field) => {
        const fieldValue = item?.[field];
        if (fieldValue === undefined || fieldValue === null) {
          return false;
        }
        return String(fieldValue).toLowerCase().includes(searchLower);
      }),
    );
  }

  if (STORE_CUSTOM_SORT[storeName]) {
    result.sort(STORE_CUSTOM_SORT[storeName]);
  } else if (sortKey) {
    const sortKeys = sortKey
      .split(",")
      .map((k) => k.trim())
      .filter(Boolean);
    const sortOrders = (sortOrder || "").split(",").map((o) => o.trim().toLowerCase());

    const isExactDateFormat = (val) => {
      if (typeof val !== "string") {
        return false;
      }
      const trimmed = val.trim();
      if (!trimmed) {
        return false;
      }

      // Match exact date formats:
      // ISO 8601 with timezone: 2025-11-21T17:00:00.000+00:00, 2025-11-21T17:00:00.000Z, 2025-11-21T17:00:00Z
      // ISO 8601 without timezone: 2025-11-21T17:00:00.000, 2025-11-21T17:00:00
      // Date only: 2025-11-21, 2025/11/21
      const datePatterns = [
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?(Z|[+-]\d{2}:\d{2})$/, // ISO 8601 with timezone
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?$/, // ISO 8601 without timezone
        /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
        /^\d{4}\/\d{2}\/\d{2}$/, // YYYY/MM/DD
      ];

      return datePatterns.some((pattern) => pattern.test(trimmed));
    };

    const getDateInfo = (val) => {
      if (typeof val === "string") {
        // Only treat as date if it matches exact date format
        if (isExactDateFormat(val)) {
          const d = new Date(val);
          return {
            date: d,
            valid: !isNaN(d.getTime()),
          };
        }
      }
      return { valid: false };
    };

    // Determine format type for each sort key by checking first non-null element
    const formatTypeCache = {};
    for (let i = 0; i < sortKeys.length; i++) {
      const key = sortKeys[i];
      // Find first non-null value for this key
      for (const item of result) {
        const value = item?.[key];
        if (value != null) {
          if (typeof value === "number") {
            formatTypeCache[key] = "number";
          } else if (typeof value === "boolean") {
            formatTypeCache[key] = "boolean";
          } else if (typeof value === "string") {
            const dateInfo = getDateInfo(value);
            formatTypeCache[key] = dateInfo.valid ? "date" : "string";
          } else {
            formatTypeCache[key] = "string";
          }
          break;
        }
      }
      // Default to string if no non-null value found
      if (!formatTypeCache[key]) {
        formatTypeCache[key] = "string";
      }
    }

    result.sort((a, b) => {
      for (let i = 0; i < sortKeys.length; i++) {
        const key = sortKeys[i];
        const order = i < sortOrders.length && ["asc", "desc"].includes(sortOrders[i]) ? sortOrders[i] : "desc";
        const formatType = formatTypeCache[key] || "string";
        let aValue = a?.[key];
        let bValue = b?.[key];

        // Null / undefined
        const aNull = aValue == null;
        const bNull = bValue == null;

        if (aNull && bNull) continue;
        if (aNull) return order === "asc" ? -1 : 1;
        if (bNull) return order === "asc" ? 1 : -1;

        // Use cached format type for comparison
        if (formatType === "number") {
          const aNum = Number(aValue);
          const bNum = Number(bValue);
          if (aNum !== bNum) return order === "asc" ? aNum - bNum : bNum - aNum;
          continue;
        }

        if (formatType === "boolean") {
          const aBool = Boolean(aValue);
          const bBool = Boolean(bValue);
          if (aBool !== bBool) return order === "asc" ? (aBool ? 1 : -1) : aBool ? -1 : 1;
          continue;
        }

        if (formatType === "date") {
          // Since format is cached as "date", directly parse without format check
          const aDate = typeof aValue === "string" ? new Date(aValue) : null;
          const bDate = typeof bValue === "string" ? new Date(bValue) : null;
          const aTime = aDate && !isNaN(aDate.getTime()) ? aDate.getTime() : null;
          const bTime = bDate && !isNaN(bDate.getTime()) ? bDate.getTime() : null;

          if (aTime != null && bTime != null) {
            const diff = aTime - bTime;
            if (diff !== 0) return order === "asc" ? diff : -diff;
            continue;
          }
          if (aTime != null) return order === "asc" ? -1 : 1;
          if (bTime != null) return order === "asc" ? 1 : -1;
        }

        // String (case-insensitive)
        if (formatType === "string") {
          const cmp = String(aValue).localeCompare(String(bValue), "en", {
            sensitivity: "accent",
            sensitivity: "base",
            numeric: true,
          });

          if (cmp !== 0) return order === "asc" ? cmp : -cmp;
          continue;
        }
      }
      return 0;
    });
  }

  const start = Math.max(startRow, 0);
  const end = pageSize != null ? start + pageSize : undefined;
  const sliced = result.slice(start, end);

  return {
    object: sliced,
    objectCount: result.length,
  };
}

function canUpdateStore(storeName, item, userIds = []) {
  if (userIds?.length === 0) {
    return true;
  }
  const permissionsRaw = localStorage.getItem(LOCAL_STORAGE.PERMISSIONS);
  let permissions = [];

  try {
    permissions = JSON.parse(permissionsRaw);
  } catch (e) {
    console.warn("Failed to parse permissions from localStorage:", permissionsRaw);
  }

  const currentUserId = getLocalStorage(LOCAL_STORAGE.USER_ID);

  const allowedRoles = STORE_PERMISSIONS[storeName] || [];

  const hasRolePermission = Array.isArray(permissions) && permissions.some((p) => allowedRoles.includes(p));

  if (hasRolePermission) {
    return true;
  }

  const storesRequireUserCheck = [
    STORES.PROJECTS,
    STORES.VERIFICATION_REPORTS,
    STORES.NOTICE_INFOS,
    STORES.MERGED_SBOMS,
    STORES.PREVIEWS,
    STORES.REQUESTS,
  ];

  // If store requires user-based filtering and user is not admin-type
  if (storesRequireUserCheck.includes(storeName)) {
    const allowedUserIds = [...userIds];
    if (storeName === STORES.PROJECTS && item.createrId) {
      allowedUserIds.push(item.createrId);
    }

    if (!allowedUserIds.includes(currentUserId?.toString())) {
      return false;
    }
  }

  return true;
}

export async function updateDataInIndexedDb(storeName, item, id, userIds) {
  if (!canUpdateStore(storeName, item, userIds)) {
    return;
  }

  const db = await getDB();
  const tx = db.transaction(storeName, "readwrite");
  const store = tx.objectStore(storeName);

  await store.put({
    ...item,
    id,
  });

  await tx.done;
}

export async function updateLocalPreferences(item = {}) {
  Object.keys(item).forEach((key) => {
    const value = item[key];
    if (value !== null && value !== undefined) {
      localStorage.setItem(key, typeof value === "object" ? JSON.stringify(value) : value);
      setClientConfigValue(key, value);
    }
  });
}

export async function deleteDataInIndexedDb(storeName, ids, userIds = []) {
  const currentUserId = getLocalStorage(LOCAL_STORAGE.USER_ID);
  const db = await getDB();
  const tx = db.transaction(storeName, "readwrite");
  const store = tx.objectStore(storeName);

  const normalizedIds = Array.isArray(ids) ? ids : [ids];

  for (const id of normalizedIds) {
    if (userIds === null || userIds.length === 0) {
      await store.delete(id);
    } else if (userIds.includes(currentUserId?.toString())) {
      await store.delete(id);
    }
  }

  await tx.done;
}

export async function deleteProjectCascadeInIndexedDb(projectIds, cascade = {}, userIds = []) {
  const projectSeqs = Array.isArray(projectIds) ? projectIds : [projectIds];

  await deleteDataInIndexedDb(STORES.PROJECTS, projectSeqs, userIds);

  if (!cascade) {
    return;
  }

  const {
    verificationReportSeqs = [],
    noticeInfoSeqs = [],
    actionIds = [],
  } = cascade;

  if (verificationReportSeqs.length > 0) {
    await deleteDataInIndexedDb(STORES.VERIFICATION_REPORTS, verificationReportSeqs, userIds);
  }

  if (noticeInfoSeqs.length > 0) {
    await deleteDataInIndexedDb(STORES.NOTICE_INFOS, noticeInfoSeqs, userIds);
  }

  if (actionIds.length > 0) {
    await deleteDataInIndexedDb(STORES.REQUESTS, actionIds, userIds);
  }
}

export async function clearStore(storeName) {
  const db = await getDB();
  const tx = db.transaction(storeName, "readwrite");
  await tx.objectStore(storeName).clear();
  await tx.done;
}

export async function clearAllIndexedDbData() {
  await Promise.all(Object.values(STORES).map((storeName) => clearStore(storeName)));
}

/**
 * Derive { count, maxUpdatedAt } from IndexedDB store contents.
 * maxUpdatedAt is epoch milliseconds (or null when the store is empty).
 */
export async function computeLocalSyncMetadata(storeName) {
  if (!storeName || storeName === STORES.SYNC_META) {
    return { count: 0, maxUpdatedAt: null };
  }
  const db = await getDB();
  const records = await db.getAll(storeName);
  const list = Array.isArray(records) ? records : [];
  const timeField = STORE_SYNC_METADATA_TIME_FIELD[storeName] || "updatedAt";
  let maxUpdatedAt = null;

  for (const item of list) {
    const epoch = toSyncMetadataEpochMs(item?.[timeField]);
    if (epoch != null && (maxUpdatedAt == null || epoch > maxUpdatedAt)) {
      maxUpdatedAt = epoch;
    }
  }

  return { count: list.length, maxUpdatedAt };
}

/** Last server-reported metadata snapshot for an entity (stored after sync/reconcile). */
export async function getStoredSyncMetadata(entity) {
  if (!entity || entity === STORES.SYNC_META) {
    return null;
  }
  const db = await getDB();
  const record = await db.get(STORES.SYNC_META, entity);
  if (!record) {
    return null;
  }
  return {
    count: Number(record.count) || 0,
    maxUpdatedAt: toSyncMetadataEpochMs(record.maxUpdatedAt),
  };
}

/** Persist server sync metadata snapshots keyed by entity name. */
export async function saveStoredSyncMetadataList(metadataList) {
  if (!Array.isArray(metadataList) || metadataList.length === 0) {
    return;
  }
  const db = await getDB();
  const tx = db.transaction(STORES.SYNC_META, "readwrite");
  const store = tx.objectStore(STORES.SYNC_META);
  const syncedAt = Date.now();

  for (const raw of metadataList) {
    const entity = raw?.entity;
    if (!entity || entity === STORES.SYNC_META) {
      continue;
    }
    await store.put({
      id: entity,
      entity,
      count: Number(raw.count) || 0,
      maxUpdatedAt: toSyncMetadataEpochMs(raw.maxUpdatedAt),
      syncedAt,
    });
  }

  await tx.done;
}
